import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface AddProductsToCatalogueScreenProps {
  catalogueId: string;
}

export const AddProductsToCatalogueScreen: React.FC<AddProductsToCatalogueScreenProps> = ({
  catalogueId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [showSelector, setShowSelector] = useState(true);
  const [adding, setAdding] = useState(false);

  const handleConfirm = async (selectedProductIds: string[]) => {
    if (selectedProductIds.length === 0) {
      showNotification('warning', 'No Selection', 'Please select at least one product');
      return;
    }

    try {
      setAdding(true);

      // Add each product to the catalogue — additive (many-to-many), doesn't
      // remove the product from any catalogue it already belongs to.
      let successCount = 0;
      let failCount = 0;

      for (const productId of selectedProductIds) {
        try {
          const response = await apiService.addProductToCatalogue(catalogueId, productId);

          if (response.statusCode === 1000) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      if (successCount > 0) {
        showNotification(
          'success',
          'Success',
          `Successfully added ${successCount} ${successCount === 1 ? 'product' : 'products'} to catalogue.${failCount > 0 ? ` ${failCount} failed.` : ''}`
        );
        setTimeout(() => {
          if (Platform.OS === 'web') {
            window.history.back();
          } else {
            navigateTo('catalogue-detail', catalogueId);
          }
        }, 1500);
      } else {
        showNotification('error', 'Error', 'Failed to add products to catalogue');
      }
    } catch (error: any) {
      console.error('Error adding products:', error);
      showNotification('error', 'Error', error.message || 'Failed to add products to catalogue');
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('catalogue-detail', catalogueId);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title="Add Products to Catalogue" onBack={handleClose} />}
    >
      <View style={styles.screen}>
        <ProductSelectorModal
          visible={showSelector}
          onClose={handleClose}
          onConfirm={handleConfirm}
          excludeCatalogueId={catalogueId}
          title="Add Products to Catalogue"
          confirmText={adding ? 'Adding...' : 'Add to Catalogue'}
          allowMultiple={true}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
});

